import { useCallback, useEffect, useState } from 'react';
import { type ApiClientStatus, ThingsApiClient } from '@lifepowr/components/src/utils/api-client';
import {
  type DeviceDynamicDataSubscriber,
  deviceDynamicDataSubscriber,
  type DeviceStateStoreService,
  deviceStateStoreService,
} from './device-dynamic-data-subscriber';

/**
 * DeviceApiClient is responsible for fetching the device data via the devices API and
 * ensuring that only one API call is made per device, essentially debouncing multiple calls to it.
 *
 * It uses an observer pattern to notify registered listeners about the status of the API call.
 *
 * @note It supports only ONE device at a time.
 */
class DeviceApiClient extends ThingsApiClient<any> {
  private dynamicDataSubscriber: DeviceDynamicDataSubscriber;
  private deviceStateStoreService: DeviceStateStoreService;

  constructor(dynamicDataSubscriber: DeviceDynamicDataSubscriber, deviceStateStoreService: DeviceStateStoreService) {
    super(true);

    this.dynamicDataSubscriber = dynamicDataSubscriber;
    this.deviceStateStoreService = deviceStateStoreService;
  }

  protected async preApiInvocationAction(): Promise<any> {
    await this.dynamicDataSubscriber.unsubscribeFromAwsAsync();

    return {};
  }

  protected async postApiInvocationAction(data: any): Promise<void> {
    this.deviceStateStoreService.updateThingData(data);

    if (this.thingName) {
      await this.dynamicDataSubscriber.subscribeToAwsAsync(this.thingName);
    }
  }

  protected async apiInvocationErrorAction(_: any): Promise<void> {}

  protected getApiUri(): string {
    return `/devices/${this.thingName}`;
  }
}

/**
 * DeviceSchemaApiClient is responsible for fetching the device schema via the devices API and
 * ensuring that only one API call is made per device, essentially debouncing multiple calls to it.
 *
 * It uses an observer pattern to notify registered listeners about the status of the API call.
 *
 * @note It supports only ONE device at a time.
 */
class DeviceSchemaApiClient extends ThingsApiClient<any> {
  constructor() {
    super(true);
  }

  protected async preApiInvocationAction(): Promise<any> {
    return {};
  }

  protected async postApiInvocationAction(_: any): Promise<void> {}

  protected async apiInvocationErrorAction(_: any): Promise<void> {}

  protected getApiUri(): string {
    return `/devices/${this.thingName}/schema`;
  }
}

const deviceApiClient = new DeviceApiClient(deviceDynamicDataSubscriber, deviceStateStoreService);
const deviceSchemaApiClient = new DeviceSchemaApiClient();

export type DeviceDataApiHookType = {
  deviceDataApiClientStatus: ApiClientStatus<any>;
  setThingName: (thingName: string) => void;
  triggerDeviceData: () => Promise<void>;
};

export function useDeviceDataApiHook(): DeviceDataApiHookType {
  const [deviceDataApiClientStatus, setDeviceDataApiClientStatus] = useState(deviceApiClient.getStatus());

  useEffect(() => {
    // Returns the unsubscribe function to stop listening to the device data API client status changes when the hook is unmounted
    return deviceApiClient.subscribe((newDeviceDataApiClientStatus) => {
      setDeviceDataApiClientStatus({ ...newDeviceDataApiClientStatus });
    });
  }, []);

  const setThingName = useCallback((thingName) => {
    deviceApiClient.fetchData(thingName).catch((e: any) => {
      console.error('Error fetching device data:', e);
    });
  }, []);

  const triggerDeviceData = useCallback(async (): Promise<void> => {
    return deviceApiClient.forceFetchData().catch((e: any) => {
      console.error('Error fetching device data:', e);
    });
  }, []);

  return {
    deviceDataApiClientStatus,
    setThingName,
    triggerDeviceData,
  };
}

export function useDeviceSchemaApiHook() {
  const [deviceSchemaApiClientStatus, setDeviceSchemaApiClientStatus] = useState(deviceSchemaApiClient.getStatus());

  useEffect(() => {
    // Returns the unsubscribe function to stop listening to the device schema API client status changes when the hook is unmounted
    return deviceSchemaApiClient.subscribe((newDeviceSchemaApiClientStatus) => {
      setDeviceSchemaApiClientStatus({ ...newDeviceSchemaApiClientStatus });
    });
  }, []);

  const setThingName = useCallback((thingName) => {
    deviceSchemaApiClient.fetchData(thingName).catch((e: any) => {
      console.error('Error fetching device schema:', e);
    });
  }, []);

  const triggerDeviceSchema = useCallback(() => {
    deviceSchemaApiClient.forceFetchData().catch((e: any) => {
      console.error('Error fetching device schema:', e);
    });
  }, []);

  return {
    deviceSchemaApiClientStatus,
    setThingName,
    triggerDeviceSchema,
  };
}
